/**
 * Shared utilities and primitives for Scene Layout components.
 * All layouts import from here — keeps individual layout files small.
 */
import React from 'react';
import { AbsoluteFill, Img, Video, useCurrentFrame, useVideoConfig, interpolate } from 'remotion';
import type { UploadedAsset, TemplateFontStyle } from '../../../types';
import type { SceneProps, ContentLayerKey, ContentLayerTransforms, ContentLayerTransform } from '../../types';
import { DEFAULT_CONTENT_LAYER_TRANSFORM } from '../../types';

// ─── Scene Context ────────────────────────────────────────────
// Allows SceneComposition to pass scene-level settings down to
// deeply nested components without prop-drilling every layout.

export interface SceneContextValue {
  videoMuted: boolean;
  hideImage: boolean;
  hideVideo: boolean;
  contentLayerTransforms?: ContentLayerTransforms;
}

export const SceneContext = React.createContext<SceneContextValue>({
  videoMuted: false,
  hideImage: false,
  hideVideo: false,
});

// ─── Layer Transform Hook ─────────────────────────────────────
// Returns effective transform for a given content layer key.

export function useLayerTransform(layerKey?: ContentLayerKey): ContentLayerTransform {
  const { contentLayerTransforms } = React.useContext(SceneContext);
  if (!layerKey) return DEFAULT_CONTENT_LAYER_TRANSFORM;
  return { ...DEFAULT_CONTENT_LAYER_TRANSFORM, ...(contentLayerTransforms?.[layerKey]) };
}

// ─── Scene fade in/out ────────────────────────────────────────

/**
 * Smooth opacity for the entire scene: fade in first 12 frames, fade out last 12 frames.
 */
export function useSceneOpacity(fadeDuration = 12): number {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();
  return interpolate(
    frame,
    [0, fadeDuration, durationInFrames - fadeDuration, durationInFrames],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );
}

/**
 * Slide + fade in for text elements.
 * Returns { opacity, transform } CSS properties.
 */
export function useTextEnter(delayFrames = 8, durationFrames = 18): React.CSSProperties {
  const frame = useCurrentFrame();
  const f = Math.max(0, frame - delayFrames);
  const p = Math.min(1, f / durationFrames);
  const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
  return {
    opacity: eased,
    transform: `translateY(${(1 - eased) * 20}px)`,
  };
}

// ─── Visual (image/video) primitive ───────────────────────────

interface VisualProps {
  asset?: UploadedAsset;
  fallbackColor?: string;
  objectFit?: 'cover' | 'contain';
  style?: React.CSSProperties;
  /** Ken Burns motion (slow zoom) */
  kenBurns?: boolean;
  kenBurnsDirection?: 'in' | 'out';
  /** Content layer key — used to look up transforms from SceneContext */
  layerKey?: ContentLayerKey;
}

export const Visual: React.FC<VisualProps> = ({
  asset, fallbackColor = '#0a1628', objectFit = 'cover', style,
  kenBurns = false, kenBurnsDirection = 'in', layerKey,
}) => {
  const { videoMuted, hideImage, hideVideo } = React.useContext(SceneContext);
  const lt = useLayerTransform(layerKey);
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();
  const progress = frame / durationInFrames;

  // Ken Burns: subtle 100%→108% zoom
  const kbScale = kenBurns
    ? kenBurnsDirection === 'in'
      ? 1 + progress * 0.08
      : 1.08 - progress * 0.08
    : 1;

  // Layer transform wrapper style
  // When explicit w/h are set (> 0), use them as absolute pixel dimensions and
  // position with translate from the layer's natural top-left origin.
  // Otherwise fall back to 100%×100% with scale (old behaviour).
  const explicitW = lt.w && lt.w > 0 ? lt.w : null;
  const explicitH = lt.h && lt.h > 0 ? lt.h : null;
  const hasExplicitSize = explicitW !== null || explicitH !== null;
  const wrapperStyle: React.CSSProperties = layerKey ? {
    width:  explicitW ? `${explicitW}px` : '100%',
    height: explicitH ? `${explicitH}px` : '100%',
    transform: `translate(${lt.dx}px, ${lt.dy}px)${!hasExplicitSize ? ` scale(${lt.scale})` : ''}`,
    transformOrigin: hasExplicitSize ? 'top left' : 'center center',
    opacity: lt.opacity,
    display: lt.visible ? undefined : 'none',
    ...(lt.zIndex != null && lt.zIndex !== 0 ? { position: 'relative', zIndex: lt.zIndex } : {}),
  } : { width: '100%', height: '100%' };

  const baseStyle: React.CSSProperties = {
    width: '100%', height: '100%',
    objectFit, display: 'block',
    transform: `scale(${kbScale})`,
    transformOrigin: 'center center',
    ...style,
  };

  const content = (() => {
    if (!asset) {
      return <div style={{ width: '100%', height: '100%', background: fallbackColor }} />;
    }
    if (asset.type === 'video') {
      if (hideVideo) return <div style={{ width: '100%', height: '100%', background: fallbackColor }} />;
      return <Video src={asset.url} style={{ ...baseStyle, transform: undefined }} muted={videoMuted} />;
    }
    if (hideImage) return <div style={{ width: '100%', height: '100%', background: fallbackColor }} />;
    return <Img src={asset.url} style={baseStyle} />;
  })();

  if (!layerKey) return content;
  return <div style={wrapperStyle}>{content}</div>;
};

// ─── Headline text ─────────────────────────────────────────────

interface HeadlineTextProps {
  text: string;
  font: TemplateFontStyle;
  delay?: number;
  style?: React.CSSProperties;
  maxLines?: number;
  /** Content layer key — used to look up transforms from SceneContext */
  layerKey?: ContentLayerKey;
}

export const HeadlineText: React.FC<HeadlineTextProps> = ({
  text, font, delay = 8, style, maxLines = 3, layerKey,
}) => {
  const enterStyle = useTextEnter(delay);
  const lt = useLayerTransform(layerKey);

  const inner = (
    <div style={{
      fontFamily: font.fontFamily,
      fontSize: font.fontSize,
      fontWeight: font.fontWeight,
      color: font.color,
      letterSpacing: font.letterSpacing ?? 0,
      lineHeight: font.lineHeight ?? 1.25,
      textTransform: (font.textTransform as React.CSSProperties['textTransform']) ?? 'none',
      display: '-webkit-box',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      ...enterStyle,
      ...style,
    }}>
      {text}
    </div>
  );

  if (!layerKey) return inner;
  if (!lt.visible) return null;
  return (
    <div style={{
      transform: `translate(${lt.dx}px, ${lt.dy}px) scale(${lt.scale})`,
      transformOrigin: 'top left',
      opacity: lt.opacity,
    }}>
      {inner}
    </div>
  );
};

// ─── Subheadline text ─────────────────────────────────────────

export const SubheadlineText: React.FC<{
  text: string; font: TemplateFontStyle; delay?: number; style?: React.CSSProperties;
  /** Content layer key — used to look up transforms from SceneContext */
  layerKey?: ContentLayerKey;
}> = ({ text, font, delay = 16, style, layerKey }) => {
  const enterStyle = useTextEnter(delay);
  const lt = useLayerTransform(layerKey);

  const inner = (
    <div style={{
      fontFamily: font.fontFamily,
      fontSize: font.fontSize,
      fontWeight: font.fontWeight,
      color: font.color,
      letterSpacing: font.letterSpacing ?? 0,
      lineHeight: font.lineHeight ?? 1.4,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      ...enterStyle,
      ...style,
    }}>
      {text}
    </div>
  );

  if (!layerKey) return inner;
  if (!lt.visible) return null;
  return (
    <div style={{
      transform: `translate(${lt.dx}px, ${lt.dy}px) scale(${lt.scale})`,
      transformOrigin: 'top left',
      opacity: lt.opacity,
    }}>
      {inner}
    </div>
  );
};

// ─── Ticker bar ───────────────────────────────────────────────

export const TickerBar: React.FC<{ text: string; color: string; textColor: string; speed?: number }> = ({
  text, color, textColor, speed = 3,
}) => {
  const frame = useCurrentFrame();
  const { width } = useVideoConfig();
  const offset = (frame * speed) % (width + text.length * 18);

  return (
    <div style={{
      position: 'absolute', bottom: 0, left: 0, right: 0,
      height: 46, background: color,
      display: 'flex', alignItems: 'center', overflow: 'hidden',
    }}>
      <div style={{
        transform: `translateX(${width - offset}px)`,
        whiteSpace: 'nowrap',
        fontFamily: 'Roboto, sans-serif', fontSize: 22, fontWeight: 700,
        color: textColor, letterSpacing: 0.5,
      }}>
        {text} &nbsp;&nbsp;●&nbsp;&nbsp; {text}
      </div>
    </div>
  );
};

// ─── Story counter ────────────────────────────────────────────

export const StoryCounter: React.FC<{
  text: string; color: string; accentColor: string;
}> = ({ text, color, accentColor }) => {
  const enterStyle = useTextEnter(0, 10);
  return (
    <div style={{
      position: 'absolute', top: 70, left: 0,
      background: color, padding: '8px 24px 8px 16px',
      borderRight: `4px solid ${accentColor}`,
      ...enterStyle,
    }}>
      <div style={{
        fontFamily: 'Roboto, sans-serif', fontSize: 20,
        fontWeight: 800, color: '#fff', letterSpacing: 1,
        textTransform: 'uppercase',
      }}>
        {text}
      </div>
    </div>
  );
};

// ─── Breaking banner ──────────────────────────────────────────

export const BreakingBanner: React.FC<{
  label: string; color: string;
}> = ({ label, color }) => {
  const frame = useCurrentFrame();
  const pulse = Math.sin(frame * 0.15) * 0.3 + 0.7;
  return (
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: 52,
      background: color, display: 'flex', alignItems: 'center',
      padding: '0 24px', gap: 12,
    }}>
      <div style={{
        width: 12, height: 12, borderRadius: '50%',
        background: '#fff', opacity: pulse, flexShrink: 0,
      }} />
      <div style={{
        fontFamily: 'Roboto, sans-serif', fontSize: 22,
        fontWeight: 900, color: '#fff', letterSpacing: 3,
        textTransform: 'uppercase',
      }}>
        {label}
      </div>
    </div>
  );
};

// ─── Dark overlay helper ──────────────────────────────────────

export const DarkOverlay: React.FC<{ opacity?: number; gradient?: string }> = ({
  opacity = 0.55, gradient,
}) => (
  <AbsoluteFill style={{
    background: gradient ?? `rgba(0,0,0,${opacity})`,
    pointerEvents: 'none',
  }} />
);

// ─── Accent bar ───────────────────────────────────────────────

export const AccentBar: React.FC<{ color: string; style?: React.CSSProperties }> = ({ color, style }) => {
  const frame = useCurrentFrame();
  const w = interpolate(frame, [0, 15], [0, 100], { extrapolateRight: 'clamp' });
  return (
    <div style={{
      height: 4, width: `${w}%`,
      background: color, borderRadius: 2, ...style,
    }} />
  );
};

// ─── Common scene container ───────────────────────────────────

export const SceneContainer: React.FC<{
  bg?: string; children: React.ReactNode;
}> = ({ bg = '#0d1f3c', children }) => {
  const opacity = useSceneOpacity();
  return (
    <AbsoluteFill style={{ background: bg, opacity }}>
      {children}
    </AbsoluteFill>
  );
};

// ─── Global overlay wrapper ────────────────────────────────────

/**
 * Renders persistent overlays (ticker, story counter, breaking banner)
 * that sit on top of every scene layout.
 */
export const GlobalOverlays: React.FC<{ props: SceneProps }> = ({ props }) => {
  const {
    showTicker, tickerText, colorPalette,
    showStoryCounter, storyCounterText,
    isBreaking, breakingLabel,
    tickerSpeed,
  } = props;

  return (
    <>
      {isBreaking && breakingLabel && (
        <BreakingBanner label={breakingLabel} color={colorPalette.breakingBanner} />
      )}
      {showStoryCounter && storyCounterText && (
        <StoryCounter
          text={storyCounterText}
          color={colorPalette.secondary}
          accentColor={colorPalette.accent}
        />
      )}
      {showTicker && tickerText && (
        <TickerBar
          text={tickerText}
          color={colorPalette.ticker}
          textColor={colorPalette.tickerText}
          speed={tickerSpeed ?? 3}
        />
      )}
    </>
  );
};
